import { Result, ok, err } from 'neverthrow'
import {
  GameState,
  UpgradeId,
  UpgradeDefinition,
  UpgradeRequirement,
} from '../types'
import { upgrades, getUpgrade } from '../data/upgrades'
import { getBuildingCount } from './buildingSystem'

export const checkUpgradeRequirement = (
  state: GameState,
  requirement: UpgradeRequirement
): boolean => {
  switch (requirement.type) {
    case 'buildingCount': {
      const owned = getBuildingCount(state, requirement.buildingId)
      return owned.gte(requirement.count)
    }
    case 'clickCount': {
      return state.totalClicks.gte(requirement.count)
    }
  }
}

export const isUpgradeAvailable = (
  state: GameState,
  upgradeId: UpgradeId
): boolean => {
  const upgrade = getUpgrade(upgradeId)
  if (upgrade === undefined) {
    return false
  }

  // Already purchased
  if (state.upgrades.has(upgradeId)) {
    return false
  }

  return checkUpgradeRequirement(state, upgrade.requirement)
}

export const canAffordUpgrade = (
  state: GameState,
  upgradeId: UpgradeId
): boolean => {
  const upgrade = getUpgrade(upgradeId)
  if (upgrade === undefined) {
    return false
  }

  // Must meet requirement first
  if (!isUpgradeAvailable(state, upgradeId)) {
    return false
  }

  return state.tokens.gte(upgrade.cost)
}

export const purchaseUpgrade = (
  state: GameState,
  upgradeId: UpgradeId
): Result<GameState, string> => {
  const upgrade = getUpgrade(upgradeId)
  if (upgrade === undefined) {
    return err(`Unknown upgrade: ${upgradeId}`)
  }

  if (state.upgrades.has(upgradeId)) {
    return err(`Already purchased: ${upgrade.name}`)
  }

  if (!checkUpgradeRequirement(state, upgrade.requirement)) {
    return err(`Requirement not met for: ${upgrade.name}`)
  }

  if (state.tokens.lt(upgrade.cost)) {
    return err(`Cannot afford ${upgrade.name}. Need ${upgrade.cost}, have ${state.tokens}`)
  }

  const newUpgrades = new Set(state.upgrades)
  newUpgrades.add(upgradeId)

  return ok({
    ...state,
    tokens: state.tokens.sub(upgrade.cost),
    upgrades: newUpgrades,
  })
}

export const getAvailableUpgrades = (state: GameState): UpgradeDefinition[] => {
  const available: UpgradeDefinition[] = []

  for (const [upgradeId, upgrade] of upgrades) {
    if (isUpgradeAvailable(state, upgradeId)) {
      available.push(upgrade)
    }
  }

  return available
}

export const getPurchasedUpgrades = (state: GameState): UpgradeDefinition[] => {
  const purchased: UpgradeDefinition[] = []

  for (const upgradeId of state.upgrades) {
    const upgrade = getUpgrade(upgradeId)
    if (upgrade !== undefined) {
      purchased.push(upgrade)
    }
  }

  return purchased
}
